import React from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IK1Field, IK1MultiValue } from "store/types";

import { K1EditValue } from "../hooks/use-k1-form";

interface Props {
  field: IK1Field;
  value: K1EditValue;
  edited: boolean;
  readOnly: boolean;
  onChange: (key: string, value: K1EditValue) => void;
}

const inputSx = (align: "left" | "right") => ({
  fontSize: "13px",
  color: "#071f35",
  bgcolor: "white",
  "& input": { textAlign: align, py: "5px", px: "8px" },
  "& fieldset": { borderColor: "#dbe8f5" },
  "&:hover fieldset": { borderColor: "#7b92a9" }
});

export const K1MultiField: React.FC<Props> = ({ field, value, edited, readOnly, onChange }) => {
  const rows: IK1MultiValue[] = Array.isArray(value) ? value : [];
  const isNumber = field.type === "number";

  const commit = (next: IK1MultiValue[]) => onChange(field.key, next);
  const setRow = (idx: number, patch: Partial<IK1MultiValue>) =>
    commit(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => commit([...rows, { key: "", value: "" }]);
  const removeRow = (idx: number) => commit(rows.filter((_, i) => i !== idx));

  return (
    <Stack
      sx={{
        px: "12px",
        py: "6px",
        gap: "6px",
        borderTop: "1px solid #eef2f8",
        "&:first-of-type": { borderTop: "none" },
        bgcolor: edited ? "#effcfd" : "transparent"
      }}
    >
      <Stack direction="row" alignItems="center">
        <Typography sx={{ flex: 1, fontSize: "13px", color: "#496682", lineHeight: 1.35 }}>{field.name}</Typography>
        {edited && (
          <MaterialTooltip variant="dark" isShow title="Value differs from the original extracted value">
            <ErrorOutlineIcon sx={{ fontSize: 16, color: "#EBAF01" }} />
          </MaterialTooltip>
        )}
      </Stack>

      {rows.map((row, idx) => (
        <Stack key={idx} direction="row" alignItems="center" gap="6px">
          <TextField
            value={row.key}
            disabled={readOnly}
            onChange={(e) => setRow(idx, { key: e.target.value })}
            placeholder="Code"
            variant="outlined"
            size="small"
            InputProps={{ sx: inputSx("left") }}
            sx={{ width: 64, flexShrink: 0, "& input": { textTransform: "uppercase" } }}
          />
          <TextField
            value={row.value}
            disabled={readOnly}
            onChange={(e) => setRow(idx, { value: e.target.value })}
            variant="outlined"
            size="small"
            InputProps={{ sx: inputSx(isNumber ? "right" : "left") }}
            sx={{ flex: 1 }}
          />
          {!readOnly && (
            <IconButton
              onClick={() => removeRow(idx)}
              sx={{ p: "2px", color: "#92a7bc", "&:hover": { color: "#d93e3e", bgcolor: "#f7e4e1" } }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Stack>
      ))}

      {!readOnly && (
        <Button
          onClick={addRow}
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          disableRipple
          sx={{
            alignSelf: "flex-start",
            minWidth: 0,
            p: 0,
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 400,
            color: "#26a6ae",
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" }
          }}
        >
          Add row
        </Button>
      )}
    </Stack>
  );
};
